"use client"

import { CommonStylesProps } from "@/(admin)/modules/ui-builder/config/lib/commonCSSProps"
import { generateResponsiveCSS } from "@/(admin)/modules/ui-builder/config/lib/helper"
import { Images } from "@/constants/images"
import { t } from "@/languages/i18n"
import { PostData } from "@/lib/config/types"
import { fetchPosts } from "@/services/postServices"
import { ComponentConfig } from "@measured/puck"
import Image from "next/image"
import Link from "next/link"
import { useEffect, useRef, useState } from "react"
import { PostCardItem } from "./PostCardItem"

export interface PostGridProps extends CommonStylesProps {
  title: string
  subTitle: string
  pageSize: string
  loadMoreUrl?: string
}

export const RenderConfig: ComponentConfig<PostGridProps> = {
  render: ({ title, subTitle, pageSize, loadMoreUrl = "", className, styles, responsiveType }) => {
    const [posts, setPosts] = useState<PostData[]>([])
    const [loading, setLoading] = useState(true)
    const [currentIndex, setCurrentIndex] = useState(0)
    const [isDragging, setIsDragging] = useState(false)
    const [startX, setStartX] = useState(0)
    const [scrollLeft, setScrollLeft] = useState(0)

    const scrollContainerRef = useRef<HTMLDivElement>(null)
    const id = `post-grid-${Math.random().toString(36).substr(2, 9)}`
    const responsiveCSS = generateResponsiveCSS(id, styles || {}, responsiveType)

    const getPosts = async () => {
      setLoading(true)
      try {
        const response = await fetchPosts({ pageSize })
        if (response?.data) {
          setPosts(response.data)
        }
      } catch (error) {
        console.error("Error loading posts:", error)
      }
      setLoading(false)
    }

    useEffect(() => {
      getPosts()
    }, [pageSize])

    // Card width + gap
    const CARD_WIDTH = 373.33
    const GAP = 24 // gap-6 = 24px

    const scrollToIndex = (index: number) => {
      if (scrollContainerRef.current) {
        const scrollPosition = index * (CARD_WIDTH + GAP)
        scrollContainerRef.current.scrollTo({
          left: scrollPosition,
          behavior: "smooth",
        })
        setCurrentIndex(index)
      }
    }

    const handlePrev = () => {
      const newIndex = Math.max(0, currentIndex - 1)
      scrollToIndex(newIndex)
    }

    const handleNext = () => {
      const newIndex = Math.min(posts.length - 1, currentIndex + 1)
      scrollToIndex(newIndex)
    }

    // Drag handlers
    const handleMouseDown = (e: React.MouseEvent) => {
      if (!scrollContainerRef.current) return
      setIsDragging(true)
      setStartX(e.pageX - scrollContainerRef.current.offsetLeft)
      setScrollLeft(scrollContainerRef.current.scrollLeft)
    }

    const handleTouchStart = (e: React.TouchEvent) => {
      if (!scrollContainerRef.current) return
      setIsDragging(true)
      setStartX(e.touches[0].pageX - scrollContainerRef.current.offsetLeft)
      setScrollLeft(scrollContainerRef.current.scrollLeft)
    }

    const handleMouseMove = (e: React.MouseEvent) => {
      if (!isDragging || !scrollContainerRef.current) return
      e.preventDefault()
      const x = e.pageX - scrollContainerRef.current.offsetLeft
      const walk = (x - startX) * 2
      scrollContainerRef.current.scrollLeft = scrollLeft - walk
    }

    const handleTouchMove = (e: React.TouchEvent) => {
      if (!isDragging || !scrollContainerRef.current) return
      const x = e.touches[0].pageX - scrollContainerRef.current.offsetLeft
      const walk = (x - startX) * 2
      scrollContainerRef.current.scrollLeft = scrollLeft - walk
    }

    const handleMouseUp = () => {
      if (!isDragging || !scrollContainerRef.current) return
      setIsDragging(false)

      // Snap to nearest card
      const scrollPosition = scrollContainerRef.current.scrollLeft
      const newIndex = Math.round(scrollPosition / (CARD_WIDTH + GAP))
      scrollToIndex(Math.min(posts.length - 1, Math.max(0, newIndex)))
    }

    const handleMouseLeave = () => {
      if (isDragging) {
        handleMouseUp()
      }
    }

    return (
      <>
        {responsiveCSS}
        <div className={`w-full ${className || ""}`}>
          <div className={`flex flex-col md:flex-row gap-16 items-start md:container md:mx-auto`} id={id}>
            <div className="h-full flex flex-col gap-4 md:gap-[191px] justify-between flex-shrink-0 px-4 md:px-0">
              <div className="flex flex-col gap-6">
                <p className="text-base leading-5.5 flex items-center">
                  <span className="h-[5px] w-[5px] bg-[#07121F] rounded-full inline-block mr-2"></span> {subTitle}
                </p>
                <h2 className="text-[#07121F] text-3.5xl leading-[44px] font-semibold text-nowrap">{title}</h2>
                <Link
                  href={loadMoreUrl}
                  className="py-3 px-8 bg-[#1E4259] hover:bg-white hover:text-[#1E4259] border border-transparent hover:border-[#1E4259] transition-all duration-300 text-white text-nowrap text-base leading-5.5 font-semibold rounded-full w-fit"
                >
                  {t("Load More")}
                </Link>
              </div>
              <div className="flex flex-row gap-2 items-start">
                <button
                  onClick={handlePrev}
                  disabled={currentIndex === 0}
                  className={`p-3 rounded-full transition-all duration-300 ${
                    currentIndex === 0 ? "opacity-30 cursor-not-allowed" : "hover:bg-gray-100 cursor-pointer"
                  }`}
                >
                  <Image src={Images.arrowRight} alt="arrow-left" width={16} height={16} className="rotate-180" />
                </button>
                <button
                  onClick={handleNext}
                  disabled={currentIndex >= posts.length - 1}
                  className={`p-3 rounded-full transition-all duration-300 bg-[#D9D9D94D] ${
                    currentIndex >= posts.length - 1
                      ? "opacity-30 cursor-not-allowed"
                      : "hover:bg-gray-100 cursor-pointer"
                  }`}
                >
                  <Image src={Images.arrowRight} alt="arrow-right" width={16} height={16} />
                </button>
              </div>
            </div>

            <div
              ref={scrollContainerRef}
              className={`flex gap-6 overflow-x-auto flex-1 w-full md:w-auto px-4 md:px-0 ${
                isDragging ? "cursor-grabbing" : "cursor-grab"
              } select-none`}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseLeave}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleMouseUp}
              style={{
                scrollbarWidth: "none",
                msOverflowStyle: "none",
              }}
            >
              {posts.map(post => (
                <PostCardItem key={post.id} post={post} isDragging={isDragging} />
              ))}
            </div>
          </div>
        </div>

        <style jsx>{`
          div::-webkit-scrollbar {
            display: none;
          }
        `}</style>
      </>
    )
  },
}

export default RenderConfig
